import type { OfflineViewState } from "@novorender/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { dataV2Api } from "apis/dataV2/dataV2Api";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { featuresConfig } from "config/features";
import { StorageKey } from "config/storage";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { reopenBimIndex } from "features/bimIndex";
import { useMinimapSyncPhase } from "features/miniMap/tiledDrawing";
import { useAbortController } from "hooks/useAbortController";
import { useSceneId } from "hooks/useSceneId";
import { selectUser } from "slices/authSlice";
import {
    selectBackendProvider,
    selectIncrementalCachingDisabled,
    selectIsOnline,
    selectIsProcoreDesign,
    selectWidgets,
} from "slices/explorer";
import { assertNever } from "utils/assertNever";
import { isPhone, isTablet } from "utils/misc";
import { isNovolibDebug } from "utils/novolibDebug";
import { getFromStorage } from "utils/storage";

import { FullSyncController, type FullSyncRequest, type OfflineRecords } from "./fullSyncController";
import { readCapturedManifestId, registerOpenSceneHostData } from "./hostData/offlineHostData";
import { useCaptureHostData } from "./hostData/useCaptureHostData";
import { legacySyncDates, recordFromState } from "./offlineRecord";
import {
    deleteAllOfflineScenes,
    deleteOfflineScene,
    enrolIncremental,
    getSceneIndexUrl,
    type OfflineActionContext,
    persistStorage,
} from "./offlineSceneActions";
import {
    offlineActions,
    selectIncrementalStorageLimit,
    selectOfflineAction,
    selectOfflineScenes,
    shouldUpdateIncrementalUsed,
} from "./offlineSlice";
import { trackOfflineFailure } from "./offlineTelemetry";
import { useEmitDownloadStatus } from "./useEmitDownloadStatus";
import { useNameIncrementalScene } from "./useNameIncrementalScene";
import { useIsProjectScene, useOfflineSceneDirName, useOfflineSceneKey } from "./useOfflineSceneKey";
import { useGetOfflineSceneName } from "./useOfflineSceneName";
import { useReplaceDataAccessWhenBimIndexLetGo } from "./useReplaceDataAccessWhenBimIndexLetGo";
import { useReplaceDataAccessWithoutOffline } from "./useReplaceDataAccessWithoutOffline";

export const GB = 1000 * 1000 * 1000;
const DEFAULT_INCREMENTAL_STORAGE_LIMIT = isPhone ? 5 * GB : isTablet ? 10 * GB : 50 * GB;

const evictionLoops = new WeakMap<OfflineViewState, Promise<boolean>>();

/**
 * Asked once per view state: a second `initEvictionLoop()` resolves `false` even in the tab that
 * holds the loop, and the effect asking runs again under StrictMode and as the scene key settles.
 */
function evictionLoopReady(offlineState: OfflineViewState): Promise<boolean> {
    let ready = evictionLoops.get(offlineState);
    if (!ready) {
        ready = offlineState.initEvictionLoop();
        evictionLoops.set(offlineState, ready);
    }
    return ready;
}

export function useHandleOffline() {
    const viewerSceneId = useSceneId();
    // The SDK takes the key; the record goes under the directory name derived from it.
    const sceneKey = useOfflineSceneKey();
    const sceneDirName = useOfflineSceneDirName();
    const isProjectScene = useIsProjectScene();
    const {
        state: { offlineWorkerState, view },
    } = useExplorerGlobals();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const action = useAppSelector(selectOfflineAction);
    const incrementalStorageLimit = useAppSelector(selectIncrementalStorageLimit);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const incrementalCachingDisabled = useAppSelector(selectIncrementalCachingDisabled);
    const isProcoreBackendProvider = useAppSelector(selectBackendProvider) === "procore";
    const isOnline = useAppSelector(selectIsOnline);
    const isOfflineWidgetOpen = useAppSelector(selectWidgets).includes(featuresConfig.offline.key);
    const watchedStatus = useAppSelector((state) => selectOfflineScenes(state)[sceneDirName]?.view.kind);
    const syncedView = useAppSelector((state) => {
        const view = selectOfflineScenes(state)[sceneDirName]?.view;
        return view?.kind === "synced" ? view : undefined;
    });
    const [abortController] = useAbortController();
    const emitDownloadStatus = useEmitDownloadStatus();
    const captureHostDataFor = useCaptureHostData();
    const replaceDataAccessWithoutOffline = useReplaceDataAccessWithoutOffline();
    const getOfflineSceneName = useGetOfflineSceneName();
    const minimap = useMinimapSyncPhase({ offlineState: offlineWorkerState, sceneKey, sceneDirName });
    useReplaceDataAccessWhenBimIndexLetGo();

    const records = useMemo<OfflineRecords>(
        () => ({
            get: (id) => store.getState().offline.scenes[id],
            set: (id, record) => dispatch(record ? offlineActions.addScene(record) : offlineActions.removeScene(id)),
        }),
        [store, dispatch],
    );
    const [fullSyncs] = useState(() => new FullSyncController(records));
    useEffect(() => () => fullSyncs.dispose(), [fullSyncs]);
    const nameWrite = useNameIncrementalScene({
        offlineState: offlineWorkerState,
        sceneKey,
        sceneDirName,
        isProjectScene,
        fullSyncs,
    });
    // An action is handled once, though the effect runs again for it: StrictMode, or a dependency
    // changing while it is handled.
    const [handledActions] = useState(() => new WeakSet<object>());

    useEffect(
        function initOfflineScenes() {
            if (!offlineWorkerState || !view) {
                return;
            }
            let mounted = true;

            const storedLimit = Number(getFromStorage(StorageKey.IncrementalOfflineLimit));
            dispatch(offlineActions.setIncrementalStorageLimit(storedLimit || DEFAULT_INCREMENTAL_STORAGE_LIMIT));

            // Otherwise an incremental record's size is only read at startup, enrolment and naming, so a
            // scene filling its cache would keep showing its old size.
            const removeSizeListener = offlineWorkerState.addEventListener(
                "incrementalSizeChange",
                ({ size, scenes }) => {
                    dispatch(offlineActions.setIncrementalStorageSize(size));
                    const changed = [...(scenes ?? [])]
                        .filter(([id, used]) => shouldUpdateIncrementalUsed(records.get(id), used))
                        .map(([id, used]) => ({ id, used }));
                    if (changed.length > 0) {
                        dispatch(offlineActions.setIncrementalUsed(changed));
                    }
                },
            );
            const removeEvictionListener = offlineWorkerState.addEventListener("sceneEvicted", ({ sceneId }) => {
                legacySyncDates.remove(sceneId);
                // A download that fails before its claim deletes the directory it made, and its failure
                // stays on the card. The SDK tells that deletion while the run is still under way.
                const shown = records.get(sceneId);
                if (shown?.runFailure && fullSyncs.isRunning) {
                    records.set(sceneId, { ...shown, view: { kind: "absent" } });
                } else if (shown?.view.kind !== "failed") {
                    dispatch(offlineActions.removeScene(sceneId));
                }
            });

            // `true` only in the tab holding the eviction lock, once its startup scan is done; the other
            // tabs' promises stay pending, and that tab's sweep covers them.
            void evictionLoopReady(offlineWorkerState).then((ready) => {
                if (ready && mounted) {
                    const thirtyDaysAgo = Date.now() - 1000 * 60 * 60 * 24 * 30;
                    offlineWorkerState.removeStaleIncrementalScenes(thirtyDaysAgo, [sceneKey]);
                }
            });

            // Every scene holding anything offline is listed, as its state reads.
            // TODO(NSO-386): restore stale-file cleanup for the open scene while it is incremental
            // (and its abort on scene delete) once NSO-386 is fixed.
            void (async () => {
                for (const scene of [...offlineWorkerState.iterateScenes()]) {
                    try {
                        const state = await scene.getState();
                        if (!mounted) {
                            return;
                        }
                        // Read before this tab's sync of the scene began, or before its run failed: the
                        // record shows those since, which what the directory read doesn't replace.
                        const shown = records.get(scene.id);
                        const ownSync = fullSyncs.hasUnsettledSync && scene.id === sceneDirName;
                        if (state.kind !== "absent" && !ownSync && !shown?.runFailure) {
                            records.set(scene.id, recordFromState(scene.id, state, false, shown));
                        }
                    } catch (e) {
                        void trackOfflineFailure({ error: e, source: "offline.useHandleOffline.getState", scene });
                    }
                }
            })();

            // Enrol the scene on first load
            if (!incrementalCachingDisabled && !offlineWorkerState.getScene(sceneKey)) {
                dispatch(offlineActions.setAction({ action: "enrolIncremental" }));
            }

            return () => {
                mounted = false;
                removeSizeListener();
                removeEvictionListener();
            };
        },
        [
            offlineWorkerState,
            dispatch,
            records,
            fullSyncs,
            sceneKey,
            sceneDirName,
            view,
            isProcoreDesign,
            incrementalCachingDisabled,
        ],
    );

    useEffect(() => {
        if (offlineWorkerState && incrementalStorageLimit !== null) {
            offlineWorkerState.setIncrementalStorageLimit(incrementalStorageLimit);
        }
    }, [offlineWorkerState, incrementalStorageLimit]);

    // The data-access layer's offline source for bookmarks, groups' ids and deviation profiles: the
    // open scene's last capture, looked up per read, so a sync completed this session is read from
    // at once. The scene open registers the same earlier, for its own reads; this one outlives it.
    useEffect(() => {
        if (!offlineWorkerState || isProcoreBackendProvider) {
            return;
        }
        return registerOpenSceneHostData({
            projectId: viewerSceneId,
            offlineState: offlineWorkerState,
            sceneKey,
            view,
            user: () => selectUser(store.getState())?.user,
        });
    }, [offlineWorkerState, isProcoreBackendProvider, viewerSceneId, sceneKey, view, store]);

    // Back online, what was read from the capture is asked of the server again: RTK Query would
    // otherwise go on showing it, and editing from it.
    const wasOffline = useRef(!isOnline);
    useEffect(() => {
        if (!isOnline) {
            wasOffline.current = true;
        } else if (wasOffline.current) {
            wasOffline.current = false;
            dispatch(dataV2Api.util.invalidateTags(["Bookmarks", "BookmarkState", "Groups", "Deviation"]));
        }
    }, [isOnline, dispatch]);

    // Another tab's sync of the open scene is seen only through `getState()`: read on mount and on
    // each `sceneStateChanged`, which the SDK tells as a sync begins and ends. Its progress isn't
    // told, so the card shows it as read when the sync began.
    useEffect(() => {
        if (!offlineWorkerState || !isProjectScene) {
            return;
        }

        let stopped = false;
        let reading = false;
        let readAgain = false;
        const read = async () => {
            const scene = offlineWorkerState.getScene(sceneKey);
            if (!scene || fullSyncs.hasUnsettledSync) {
                return;
            }
            if (reading) {
                readAgain = true;
                return;
            }
            reading = true;
            try {
                const state = await scene.getState();
                // Only what another tab changed, so this tab's own outcomes (a failure's error, say)
                // aren't replaced by what the directory reads after them.
                const shown = records.get(scene.id);
                if (!stopped && !fullSyncs.hasUnsettledSync && (state.kind === "syncing" || shown?.syncingElsewhere)) {
                    records.set(scene.id, recordFromState(scene.id, state, false, shown));
                }
            } catch {
                // The next event reads again.
            } finally {
                reading = false;
            }
            if (readAgain && !stopped) {
                readAgain = false;
                void read();
            }
        };

        const removeListener = offlineWorkerState.addEventListener("sceneStateChanged", ({ sceneId }) => {
            if (sceneId === sceneDirName) {
                void read();
            }
        });
        void read();
        return () => {
            stopped = true;
            removeListener();
        };
    }, [offlineWorkerState, isProjectScene, sceneKey, sceneDirName, records, fullSyncs]);

    // The host is told the open scene's state once per scene, so its downloaded mark follows what is
    // actually stored.
    const reportedOpenScene = useRef<string>();
    useEffect(() => {
        if (!offlineWorkerState || !view || !isProjectScene || reportedOpenScene.current === sceneDirName) {
            return;
        }

        let stopped = false;
        const scene = offlineWorkerState.getScene(sceneKey);
        void (async () => {
            try {
                const state = await scene?.getState();
                if (!stopped) {
                    reportedOpenScene.current = sceneDirName;
                    emitDownloadStatus("sceneOpen", { status: state?.kind === "synced" ? "synchronized" : "deleted" });
                }
            } catch (e) {
                void trackOfflineFailure({ error: e, source: "offline.useHandleOffline.sceneOpenStatus", scene });
            }
        })();
        return () => {
            stopped = true;
        };
    }, [offlineWorkerState, view, isProjectScene, sceneKey, sceneDirName, emitDownloadStatus]);

    // An estimate is of the scene as it stood when asked. It is dropped when the widget closes or
    // the scene's state changes kind, rather than offered later.
    useEffect(() => {
        if (store.getState().offline.estimate) {
            dispatch(offlineActions.setEstimate(undefined));
        }
    }, [watchedStatus, isOfflineWidgetOpen, store, dispatch]);

    // A failed enrolment is kept while the widget is closed, as the automatic cache enrols on load
    // with the widget closed. It is dropped when the scene's state changes kind, or by the next
    // enrolment.
    useEffect(() => {
        if (store.getState().offline.incrementalFailure) {
            dispatch(offlineActions.setIncrementalFailure(undefined));
        }
    }, [watchedStatus, store, dispatch]);

    // The version the committed capture is of, for the "bookmarks and groups need a re-sync"
    // warning: read from the directory whenever the record shows another synced state.
    useEffect(() => {
        const scene = syncedView && isProjectScene ? offlineWorkerState?.getScene(sceneKey) : undefined;
        if (!scene || isProcoreBackendProvider) {
            return;
        }
        let stopped = false;
        void readCapturedManifestId(scene.hostData, viewerSceneId).then((capturedManifestId) => {
            if (!stopped) {
                dispatch(offlineActions.updateScene({ id: scene.id, updates: { capturedManifestId } }));
            }
        });
        return () => {
            stopped = true;
        };
    }, [offlineWorkerState, syncedView, isProcoreBackendProvider, isProjectScene, sceneKey, viewerSceneId, dispatch]);

    const fullSyncRequest = useCallback(
        (handled: object): FullSyncRequest | undefined =>
            offlineWorkerState &&
            view && {
                sceneDirName,
                openScene: () => offlineWorkerState.scene(sceneKey),
                persistStorage,
                sceneIndexUrl: () => getSceneIndexUrl(view),
                name: getOfflineSceneName,
                ...(minimap.estimateBytes && { minimapBytes: minimap.estimateBytes }),
                lockHeld: nameWrite,
                options: (scene, sceneIndexUrl) => ({
                    ...(minimap.syncMinimap && { syncMinimap: minimap.syncMinimap(scene) }),
                    // Captured inside the sync and committed with it; the Procore backend has none.
                    ...(!isProcoreBackendProvider && {
                        captureHostData: captureHostDataFor(viewerSceneId, view, sceneIndexUrl),
                    }),
                    // An index open on the scene moves to the folder the sync stores, so that the
                    // sweep which follows removes the old one.
                    committed: () => reopenBimIndex(scene.id),
                }),
                reportToHost: (update) => emitDownloadStatus("fullSync", update),
                translate: t,
                // Standalone offers Pause only while no action is set; Procore keeps its Download
                // spinner for the whole sync.
                onBegin: () => {
                    if (!isProcoreDesign && store.getState().offline.action === handled) {
                        dispatch(offlineActions.setAction(undefined));
                    }
                },
            },
        [
            offlineWorkerState,
            view,
            sceneKey,
            sceneDirName,
            getOfflineSceneName,
            nameWrite,
            minimap,
            isProcoreBackendProvider,
            captureHostDataFor,
            viewerSceneId,
            emitDownloadStatus,
            t,
            isProcoreDesign,
            store,
            dispatch,
        ],
    );

    const actionContext = useMemo<OfflineActionContext | undefined>(
        () =>
            offlineWorkerState &&
            view && { offlineState: offlineWorkerState, view, sceneKey, sceneDirName, store, dispatch },
        [offlineWorkerState, view, sceneKey, sceneDirName, store, dispatch],
    );

    useEffect(() => {
        // Offline is managed on the project scene alone, which init has to resolve first.
        if (!actionContext || !action || isProjectScene === undefined || handledActions.has(action)) {
            return;
        }
        handledActions.add(action);
        const context = actionContext;
        const signal = abortController.current.signal;

        void (async () => {
            switch (action.action) {
                case "delete": {
                    const id = action.id ?? context.sceneDirName;
                    const deleted = await deleteOfflineScene(context, id, {
                        emit: emitDownloadStatus,
                        replaceDataAccess: replaceDataAccessWithoutOffline,
                        lockHeld: nameWrite,
                    });
                    if (deleted && id === context.sceneDirName && isProcoreDesign && !incrementalCachingDisabled) {
                        context.dispatch(offlineActions.setAction({ action: "enrolIncremental" }));
                    }
                    break;
                }
                case "deleteAll": {
                    // Debug utils only: it wipes every scene on the device.
                    if (!isNovolibDebug()) {
                        break;
                    }
                    const deleted = await deleteAllOfflineScenes(context, {
                        replaceDataAccess: replaceDataAccessWithoutOffline,
                        lockHeld: nameWrite,
                    });
                    if (deleted && isProcoreDesign && !incrementalCachingDisabled) {
                        context.dispatch(offlineActions.setAction({ action: "enrolIncremental" }));
                    }
                    break;
                }
                case "pause":
                    fullSyncs.pause();
                    break;
                case "enrolIncremental":
                    // The iOS host's kill switch; standalone's opt-in is unaffected.
                    if (isProjectScene && !incrementalCachingDisabled) {
                        await enrolIncremental(context, {
                            signal,
                            persistStorage,
                            pruneEnrolled: (scene, state) => minimap.pruneEnrolled(scene, state, signal),
                        });
                    }
                    break;
                case "fullSync": {
                    const request = isProjectScene ? fullSyncRequest(action) : undefined;
                    if (request) {
                        await fullSyncs.start(request);
                    }
                    break;
                }
                default:
                    assertNever(action.action);
            }
            // Only the action handled here: a sync ends long after it began, and the user may have
            // asked for something else meanwhile.
            if (context.store.getState().offline.action === action) {
                context.dispatch(offlineActions.setAction(undefined));
            }
        })();
    }, [
        action,
        actionContext,
        isProjectScene,
        handledActions,
        abortController,
        emitDownloadStatus,
        replaceDataAccessWithoutOffline,
        nameWrite,
        isProcoreDesign,
        incrementalCachingDisabled,
        fullSyncs,
        minimap,
        fullSyncRequest,
    ]);
}
